/**
 * loginHS 공개 API v1 문서 — /?page=api-docs (로그인 무관)
 * 내부 구현(엔진/서버/DB) 언급 금지. 키는 플레이스홀더(YOUR_API_KEY)만.
 * ※ 실제 구현과 1:1 정합 유지 — 미구현 기능(rate-limit·비동기 jobs·requestId)은 문서에 싣지 않는다.
 */
import React, { useEffect, useState } from "react";
import ApiPortalLayout, { SideTocItem } from "./ApiPortalLayout";

const S = {
  page: {
    maxWidth: 880,
    color: "#1f2937", // 셸(ApiPortalLayout)이 프레임 제공 — 내부 폭만 제한

    fontFamily: "'Inter','Pretendard',sans-serif",
    lineHeight: 1.65,
  },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  h2: {
    fontSize: 20,
    fontWeight: 700,
    margin: "36px 0 10px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 6,
    scrollMarginTop: 72,
  },
  h3: { fontSize: 15, fontWeight: 700, margin: "16px 0 6px" },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  ol: { fontSize: 14, color: "#374151", margin: "6px 0 6px 18px", padding: 0 },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    padding: 14,
    borderRadius: "0 0 8px 8px",
    fontSize: 12.5,
    overflowX: "auto",
    whiteSpace: "pre",
    margin: 0,
  },
  preSolo: {
    background: "#0f172a",
    color: "#e2e8f0",
    padding: 14,
    borderRadius: 8,
    fontSize: 12.5,
    overflowX: "auto",
    whiteSpace: "pre",
  },
  tabbar: {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
    background: "#1e293b",
    borderRadius: "8px 8px 0 0",
    padding: "6px 6px 0",
  },
  tab: {
    background: "transparent",
    color: "#94a3b8",
    border: "none",
    borderRadius: "6px 6px 0 0",
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tabActive: { background: "#0f172a", color: "#e2e8f0" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13.5,
    margin: "8px 0",
  },
  th: {
    textAlign: "left",
    padding: "7px 10px",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "7px 10px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "top",
  },
  code: {
    background: "#f3f4f6",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 12.5,
    fontFamily: "ui-monospace,monospace",
  },
  btn: {
    background: "#111827",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  note: {
    background: "#f8fafc",
    borderLeft: "3px solid #94a3b8",
    padding: "10px 14px",
    fontSize: 13,
    color: "#475569",
    borderRadius: 4,
    margin: "10px 0",
  },
};

function T({ head, rows }) {
  return (
    <table style={S.table}>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} style={S.th}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} style={S.td}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// 언어별 코드 탭 — EXAMPLES 배열에 항목만 추가하면 탭이 늘어난다.
function LangTabs({ examples }) {
  const [active, setActive] = useState(examples[0].key);
  const cur = examples.find((e) => e.key === active) || examples[0];
  return (
    <div>
      <div style={S.tabbar}>
        {examples.map((e) => (
          <button
            key={e.key}
            onClick={() => setActive(e.key)}
            style={{ ...S.tab, ...(active === e.key ? S.tabActive : {}) }}
          >
            {e.label}
          </button>
        ))}
      </div>
      <pre style={S.pre}>{cur.code}</pre>
    </div>
  );
}

const TOC = [
  ["#sec-1", "개요"],
  ["#sec-2", "인증 · 키 발급"],
  ["#sec-quickstart", "개발자 빠른 시작"],
  ["#sec-3", "요청"],
  ["#sec-4", "요청 예제"],
  ["#sec-5", "응답"],
  ["#sec-6", "에러 응답"],
  ["#sec-7", "버전 · 문의"],
];

// 개발자 연동 예제 — 실제 계약(X-Api-Key · 20건/요청 · results[]) 기준.
const JAVA_EXAMPLE = `// lcsv2 백엔드 (Java 11+, 서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
// HttpClient는 JDK 내장. JSON은 Jackson(com.fasterxml.jackson.databind) 사용.
import com.fasterxml.jackson.databind.*;
import com.fasterxml.jackson.databind.node.*;
import java.net.URI;
import java.net.http.*;
import java.util.*;

public class LoginHsClassifier {
  static final String API_KEY = System.getenv("LOGINHS_API_KEY");  // "lhs_..."
  static final String ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk";
  static final HttpClient HTTP = HttpClient.newHttpClient();
  static final ObjectMapper OM = new ObjectMapper();

  static List<JsonNode> classify(List<String> keywords) throws Exception {
    List<JsonNode> out = new ArrayList<>();
    // 요청당 최대 20건 → 20건씩 잘라 순차 호출
    for (int i = 0; i < keywords.size(); i += 20) {
      List<String> chunk = keywords.subList(i, Math.min(i + 20, keywords.size()));
      ArrayNode items = OM.createArrayNode();
      for (int j = 0; j < chunk.size(); j++)
        items.add(OM.createObjectNode().put("id", String.valueOf(i + j)).put("keyword", chunk.get(j)));
      ObjectNode payload = OM.createObjectNode();
      payload.set("items", items);
      HttpRequest req = HttpRequest.newBuilder(URI.create(ENDPOINT))
          .header("Content-Type", "application/json")
          .header("X-Api-Key", API_KEY)
          .POST(HttpRequest.BodyPublishers.ofString(OM.writeValueAsString(payload)))
          .build();
      HttpResponse<String> res = HTTP.send(req, HttpResponse.BodyHandlers.ofString());
      if (res.statusCode() != 200)
        throw new RuntimeException("classify HTTP " + res.statusCode() + " " + res.body());
      OM.readTree(res.body()).get("results").forEach(out::add);  // {requestId, version, results, summary}
    }
    return out;
  }

  public static void main(String[] a) throws Exception {
    for (JsonNode r : classify(List.of("가죽 가방", "잠수복"))) {
      if ("error".equals(r.get("status").asText()))
        System.out.println("실패: " + r.get("keyword").asText() + " " + r.get("error").asText());
      else if (r.get("review_needed").asBoolean())
        System.out.println("검토 필요: " + r.get("keyword").asText() + " 후보: " + r.get("top_candidates"));
      else
        System.out.println(r.get("keyword").asText() + " -> " + r.get("hsk").asText());
    }
  }
}`;

const NODE_EXAMPLE = `// Node 18+ (서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
const API_KEY = process.env.LOGINHS_API_KEY;   // "lhs_..."  (발급 화면에서 1회 표시)
const ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk";

async function classify(keywords) {
  const out = [];
  // 요청당 최대 20건 → 20건씩 잘라 순차 호출
  for (let i = 0; i < keywords.length; i += 20) {
    const items = keywords.slice(i, i + 20).map((kw, j) => ({
      id: String(i + j),   // 응답에 그대로 echo (내 라인번호와 매칭용)
      keyword: kw,
    }));
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Api-Key": API_KEY },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error("classify HTTP " + res.status + " " + (await res.text()));
    const data = await res.json();     // { requestId, version, results, summary }
    out.push(...data.results);
  }
  return out;
}

// 사용
const rows = await classify(["가죽 가방", "잠수복"]);
for (const r of rows) {
  if (r.status === "error") console.log("실패:", r.keyword, r.error);
  else if (r.review_needed) console.log("검토 필요:", r.keyword, "후보:", r.top_candidates);
  else console.log(r.keyword, "->", r.hsk, "(" + r.name_ko + ")");
}`;

const PY_EXAMPLE = `# Python 3 (서버-투-서버) — 브라우저에서 직접 호출 금지(CORS 미지원)
import os, requests

API_KEY = os.environ["LOGINHS_API_KEY"]   # "lhs_..."
ENDPOINT = "https://loginhs.tlogin.net/api/v1/classify/bulk"

def classify(keywords):
    out = []
    # 요청당 최대 20건 → 20건씩 잘라 순차 호출
    for i in range(0, len(keywords), 20):
        items = [{"id": str(i + j), "keyword": kw}
                 for j, kw in enumerate(keywords[i:i + 20])]
        r = requests.post(ENDPOINT,
                          headers={"X-Api-Key": API_KEY},
                          json={"items": items}, timeout=180)
        r.raise_for_status()               # 401/400/500 -> 예외
        out.extend(r.json()["results"])    # {requestId, version, results, summary}
    return out

# 사용
for row in classify(["가죽 가방", "잠수복"]):
    if row["status"] == "error":
        print("실패:", row["keyword"], row["error"])
    elif row["review_needed"]:
        print("검토 필요:", row["keyword"], "후보:", row["top_candidates"])
    else:
        print(row["keyword"], "->", row["hsk"], "(" + str(row["name_ko"]) + ")")`;

// 탭 순서 = 표시 순서. 언어 추가 시 여기에 { key, label, code } 만 넣으면 됨.
const CODE_EXAMPLES = [
  { key: "java", label: "Java (메인)", code: JAVA_EXAMPLE },
  { key: "node", label: "Node.js", code: NODE_EXAMPLE },
  { key: "python", label: "Python", code: PY_EXAMPLE },
];

export default function ApiDocsPage({ onNavigate }) {
  const C = (t) => <code style={S.code}>{t}</code>;
  // 딥링크(#sec-N) — 가이드 Q&A 인용 칩 등 외부 진입 시 해당 섹션으로 스크롤
  useEffect(() => {
    const h = window.location.hash;
    if (h) {
      const el = document.querySelector(h);
      if (el) setTimeout(() => el.scrollIntoView({ block: "start" }), 50);
    }
  }, []);
  return (
    <ApiPortalLayout
      active="docs"
      onNavigate={onNavigate}
      toc={TOC.map(([href, label]) => (
        <SideTocItem key={href} href={href}>
          {label}
        </SideTocItem>
      ))}
    >
      <div style={S.page}>
        <h1 style={S.h1}>loginHS 세번분류 API v1</h1>
        <p style={S.p}>
          여러 품명을 한 번의 요청으로 HS 세번(HSK 10자리)까지 분류합니다. 확신
          건은 자동 확정, 애매한 건은 <b>검토 필요(review_needed)</b> 플래그와
          후보 목록으로 반환되어 사람 검토 큐에 연결할 수 있습니다.
        </p>

        <h2 id="sec-1" style={S.h2}>
          개요
        </h2>
        <T
          head={["항목", "내용"]}
          rows={[
            [
              "기능",
              "품명 목록 일괄 HS 세번분류 (확신=자동 · 애매=검토 플래그)",
            ],
            [
              "Method / URL",
              <span>
                {C("POST")}{" "}
                {C("https://loginhs.tlogin.net/api/v1/classify/bulk")}
              </span>,
            ],
            ["Content-Type", C("application/json")],
            ["인증", <span>{C("X-Api-Key")} 헤더 (API 키 필요)</span>],
            ["호출 방식", "서버-투-서버 (브라우저 직접 호출용 CORS 미지원)"],
          ]}
        />

        <h2 id="sec-2" style={S.h2}>
          인증 · 키 발급
        </h2>
        <pre style={S.preSolo}>{`X-Api-Key: YOUR_API_KEY`}</pre>
        <p style={S.p}>
          키가 없거나 유효하지 않으면 {C("401 Unauthorized")} 가 반환됩니다.
          키는 로그인 후 아래에서 무료로 발급할 수 있습니다(베타). 발급 순간
          1회만 표시되니 안전하게 보관하세요. 키는 {C("lhs_")} 로 시작합니다.
        </p>
        <button style={S.btn} onClick={() => onNavigate("api-keys")}>
          API 키 발급 →
        </button>
        <span style={{ marginLeft: 12 }}>
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            발급/연동 문의
          </a>
        </span>

        <h2 id="sec-quickstart" style={S.h2}>
          개발자 빠른 시작
        </h2>
        <p style={S.p}>
          연동 담당 개발자는 아래 3단계만 하면 됩니다. (호출은 반드시{" "}
          <b>여러분의 백엔드 서버에서</b> — 브라우저에서 직접 호출하면 CORS 로
          막힙니다.)
        </p>
        <ol style={S.ol}>
          <li>
            <b>키 발급</b> — 위 <b>[API 키 발급]</b> 버튼 → 로그인 →{" "}
            {C("lhs_…")} 키를 복사해 서버 환경변수(예 {C("LOGINHS_API_KEY")})에
            저장.
          </li>
          <li>
            <b>백엔드에서 호출</b> — 아래 예제(탭에서 언어 선택)를 복사해
            붙여넣고 실행. 품명 배열만 넘기면 20건씩 나눠 호출합니다.
          </li>
          <li>
            <b>결과 사용</b> — {C("results[].hsk")}(10자리 세번) 사용,{" "}
            {C("review_needed:true")} 면 사람 검토로 넘기고{" "}
            {C("top_candidates")} 를 후보로 보여주기.
          </li>
        </ol>
        <LangTabs examples={CODE_EXAMPLES} />
        <div style={S.note}>
          막히거나 궁금한 점은 <b>디스코드</b> 로 물어보세요. (초대 링크는 준비
          중 — 우선{" "}
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            연동 문의
          </a>{" "}
          로 남겨주시면 담당자가 회신합니다.)
        </div>

        <h2 id="sec-3" style={S.h2}>
          요청
        </h2>
        <T
          head={["파라미터", "타입", "필수", "설명"]}
          rows={[
            [C("items"), "Array", "필수", "분류할 품명 목록 (1~20개)"],
            [
              C("items[].id"),
              "String",
              "선택",
              "라인 식별자 — 응답에 그대로 echo (생략 시 배열 순번)",
            ],
            [
              C("items[].keyword"),
              "String",
              "필수*",
              "품명 한 줄. 가진 정보(거래품명·규격·성분 등)를 띄어쓰기로 이어 붙여 넣어도 됩니다. *trade_name 이나 specs 를 보내면 생략 가능",
            ],
            [C("items[].trade_name"), "String", "선택", "거래품명 (최대 200자)"],
            [C("items[].specs"), "String[]", "선택", "규격1~10을 순서대로 (최대 10칸, 빈 칸은 빈 문자열)"],
            [C("items[].components"), "String[]", "선택", "성분1~3 (최대 3칸)"],
            [C("items[].partner"), "String", "선택", "무역거래처(해외 공급자·구매자) 상호"],
            [C("items[].partner_country / origin"), "String", "선택", "무역거래처 나라 · 원산지 (ISO 2자리)"],
            [C("items[].model / part_no"), "String", "선택", "모델·상표 · Part No"],
            [C("items[].direction"), "String", "선택", '"import" · "export"'],
            [
              C("items[].facts"),
              "Object",
              "선택",
              "결정트리가 요청한 확인값. 의류 construction·garment_type, 신발 upper_material·outer_sole_material, 기계 item_role·machine_family·self_propelled_status 등의 정규값을 전달",
            ],
            [
              C("options.decision_tree_shadow"),
              "Boolean",
              "선택",
              "독립 상위 결정트리를 명시적으로 실행. facts가 있거나 지원 류 신호가 있으면 자동으로도 실행",
            ],
            [
              C("options.apparel_tree_shadow"),
              "Boolean",
              "선택",
              "제61·62류 초기 연동 호환 옵션",
            ],
            [
              C("options.prove"),
              "Boolean",
              "선택",
              "PROVE 모델 제안(prove_hsk) 여부 — 기본 true",
            ],
          ]}
        />
        <T
          head={["제한", "값"]}
          rows={[
            ["요청당 최대 항목", "20 items (초과 시 400)"],
            ["처리 시간", "항목당 수 초 (실 검증 분류 — 즉답형 아님)"],
          ]}
        />
        <div style={S.note}>
          20건을 넘는 대량은 <b>20건씩 나눠</b> 순차 호출하세요. (베타 기간 별도
          호출량 제한은 없으나, 서버 부하를 고려해 동시 호출은 소수로 유지
          권장.)
        </div>

        <h2 id="sec-4" style={S.h2}>
          요청 예제
        </h2>
        <pre
          style={S.preSolo}
        >{`curl -X POST https://loginhs.tlogin.net/api/v1/classify/bulk \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{
    "items": [
      { "id": "line-1", "keyword": "가죽 가방" },
      { "id": "line-2", "keyword": "잠수복" },
      { "id": "line-3", "keyword": "DC 밧데리 충전기 자동자료처리기계용" },
      { "id": "apparel-1", "keyword": "셔츠", "facts": {
        "construction": "woven", "garment_type": "shirt",
        "garment_gender": "men", "textile_material": "cotton"
      } },
      { "id": "footwear-1", "keyword": "가죽 갑피 운동화", "facts": {
        "item_kind": "footwear", "upper_material": "leather",
        "outer_sole_material": "rubber_plastics"
      } },
      { "id": "machinery-1", "keyword": "AERIAL LIFT SJ3-3219", "facts": {
        "item_kind": "machinery", "item_role": "complete_machine",
        "machine_family": "self_propelled_elevating_work_platform",
        "work_platform_status": true, "lifting_or_handling_equipment_status": true,
        "self_propelled_status": true, "travel_drive_electric_status": true
      } }
    ],
    "options": { "decision_tree_shadow": true }
  }'`}</pre>
        <pre style={S.preSolo}>{`POST /api/v1/classify/bulk HTTP/1.1
Host: loginhs.tlogin.net
X-Api-Key: YOUR_API_KEY
Content-Type: application/json

{ "items": [ { "id": "line-1", "keyword": "가죽 가방" } ] }`}</pre>
        <p style={S.p}>
          신고서 칸을 가지고 있다면 칸 그대로 보내 주세요. 모든 칸은 선택이며 있는 만큼만 보내면 됩니다.
          칸 이름을 모르면 가진 정보를 띄어쓰기로 이어 붙여 {C("keyword")} 한 칸에 넣어도 됩니다.
          칸으로 보내면 학습 때와 같은 모양으로 읽어 세번 정답률이 조금 더 높습니다.
        </p>
        <pre style={S.preSolo}>{`{ "items": [ {
    "id": "lan-001-1",
    "trade_name": "PAPER BAGS",
    "specs": ["518775-00", "TIF WATCH BG PPR", "11X9.5X6.5"],
    "components": ["PAPER 100%"],
    "partner": "DSV SOLUTIONS PTE LTD", "partner_country": "SG",
    "origin": "CN", "direction": "import"
} ] }`}</pre>

        <h2 id="sec-5" style={S.h2}>
          응답
        </h2>
        <pre style={S.preSolo}>{`{
  "version": "v1",
  "results": [
    {
      "id": "line-1", "keyword": "가죽 가방", "status": "ok",
      "hsk": "4202211090", "hs6": "420221", "hs4": "4202", "chapter": "42",
      "name_ko": "기타", "confidence": "high", "score": 0.673,
      "decision_label": "HIGH (즉시 결정)",
      "review_needed": false, "top_candidates": [],
      "prove_hsk": "4202211090", "prove_hs6": "420221", "prove_hs4": "4202",
      "error": null
    },
    {
      "id": "line-2", "keyword": "잠수복", "status": "ok",
      "hsk": null, "confidence": null,
      "decision_label": "보류 (재질·구성 확인 필요)",
      "review_needed": true,
      "top_candidates": [ { "hsk": "6113001000", "name_ko": "고무를 입힌 편물제" } ],
      "error": null
    },
    {
      "id": "line-3", "keyword": "DC 밧데리 충전기 자동자료처리기계용", "status": "ok",
      "hsk": "8504401010", "hs6": "850440", "confidence": "medium",
      "review_needed": true,
      "subdivision_shadow": {
        "shadow": true, "proposed_hsk": "8504401010",
        "proposed_path_supported": false,
        "supported_hsks": ["8504403010"], "supported_hsk": "8504403010",
        "conflict": true, "requires_review": true, "action": "review_conflict",
        "questions": []
      },
      "error": null
    },
    {
      "id": "apparel-1", "keyword": "셔츠", "status": "ok",
      "decision_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "supported",
        "supported_chapter": "62", "supported_hs4": "6205",
        "supported_hs6": "620520", "supported_hsk": "6205200000",
        "questions": []
      },
      "apparel_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "supported",
        "supported_chapter": "62", "supported_hs4": "6205",
        "supported_hs6": "620520", "supported_hsk": "6205200000",
        "questions": []
      },
      "error": null
    },
    {
      "id": "footwear-1", "keyword": "가죽 갑피 운동화", "status": "ok",
      "decision_tree_shadow": {
        "shadow": true, "uses_legacy_as_input": false, "action": "ask",
        "supported_chapter": "64", "supported_hs4": "6403",
        "questions": [ { "fact_key": "sports_footwear_status",
          "options": ["yes", "no"] } ]
      },
      "error": null
    },
    {
      "id": "machinery-1", "keyword": "AERIAL LIFT SJ3-3219", "status": "ok",
      "decision_tree_shadow": {
        "shadow": true, "router_connected": true,
        "uses_legacy_as_input": false, "action": "ask",
        "supported_chapter": "84", "supported_hs4": "8427",
        "supported_hs6": "842710", "questions": []
      },
      "machinery_tree_shadow": {
        "shadow": true, "router_connected": true, "action": "ask",
        "supported_hs4": "8427", "supported_hs6": "842710"
      },
      "error": null
    }
  ],
  "summary": { "total": 6, "auto": 3, "review": 3, "failed": 0,
    "subdivision_conflicts": 1, "decision_tree_conflicts": 0,
    "apparel_tree_conflicts": 0, "footwear_tree_conflicts": 0,
    "machinery_tree_conflicts": 0, "elapsed_ms": 12400 }
}`}</pre>
        <T
          head={["필드", "타입", "설명"]}
          rows={[
            [C("version"), "String", 'API 버전 — 현재 "v1"'],
            [C("results[].id"), "String", "요청 items[].id echo (없으면 순번)"],
            [C("results[].status"), "String", '"ok" · "error"'],
            [
              C("results[].hsk"),
              "String|null",
              "HSK 10자리 (검토 필요 시 null 가능)",
            ],
            [
              C("results[].hs6 / hs4 / chapter"),
              "String|null",
              "HS 6자리 / 4자리(호) / 류",
            ],
            [C("results[].name_ko"), "String|null", "결정 세번의 한글 품목명"],
            [C("results[].confidence"), "String|null", "high · medium · low"],
            [C("results[].score"), "Number|null", "신뢰 점수 (0~1)"],
            [
              C("results[].decision_label"),
              "String|null",
              "결정 요약 라벨 (예: HIGH (즉시 결정) · 보류 …)",
            ],
            [
              C("results[].review_needed"),
              "Boolean",
              "true = 사람 검토 권장 (보류/저신뢰)",
            ],
            [C("results[].top_candidates"), "Array", "검토용 후보 세번 목록"],
            [
              C("results[].prove_hsk / prove_hs6 / prove_hs4"),
              "String|null",
              "PROVE 모델이 제안한 세번 — hsk 와 별개인 제안이며 확정이 아닙니다. 모델을 쓸 수 없을 때는 null",
            ],
            [
              C("results[].subdivision_shadow"),
              "Object|생략",
              "국내세분 결정표가 있는 HS6의 비교용 판정. 기존 hsk를 변경하지 않음",
            ],
            [
              C("results[].subdivision_shadow.supported_hsk"),
              "String|생략",
              "비차단 지지 잎이 정확히 하나일 때의 HSK10 비교 후보",
            ],
            [
              C("results[].subdivision_shadow.conflict"),
              "Boolean",
              "true = 엔진 제안과 결정표의 유일 지지 잎이 달라 수기 검토 필요",
            ],
            [
              C("results[].decision_tree_shadow"),
              "Object|생략",
              "제61·62·64류와 제84류 8425~8431의 류→HS4→HS6→HSK10 독립 결정트리. 기존 hsk는 입력으로 쓰지 않으며 계산 후 비교만 함",
            ],
            [
              C("results[].decision_tree_shadow.questions"),
              "Array",
              "다음 요청의 items[].facts로 답할 확인 축과 선택지",
            ],
            [
              C("results[].apparel_tree_shadow / footwear_tree_shadow / machinery_tree_shadow"),
              "Object|생략",
              "도메인별 호환·진단용 별칭. 공통 소비자는 decision_tree_shadow를 사용",
            ],
            [
              C("results[].error"),
              "String|null",
              "해당 항목 실패 사유 (배치는 계속 처리됨)",
            ],
            [
              C("summary"),
              "Object",
              "total / auto / review / failed와 subdivision·decision_tree·apparel·footwear·machinery 충돌 수 / elapsed_ms",
            ],
          ]}
        />
        <div style={S.note}>
          개별 항목의 오류(빈 품명 등)는 전체 요청을 실패시키지 않습니다 — 해당
          항목만 {C("status")} 가 {C('"error"')} 이고 {C("error")} 에 사유가
          담깁니다(그 외 항목은 정상 처리).
        </div>
        <div style={S.note}>
          {C("subdivision_shadow")}는 수기 비교용이며 {C("shadow:true")}입니다.
          {C("supported_hsk")}가 있어도 기존 {C("hsk")}를 자동으로 덮어쓰지 마세요.
          특히 {C("conflict:true")}는 정답 확정이 아니라 규격·용도 재확인 신호입니다.
        </div>
        <div style={S.note}>
          {C("decision_tree_shadow")}는 현재 제61·62·64류와 제84류 제8425호~제8431호에서 동작합니다. {C("action:ask")}
          또는 {C("ambiguous")}이면 {C("questions[].fact_key")}의 답을 같은 품목의
          {C("facts")}에 넣어 다시 호출하세요. 이 shadow도 기존 {C("hsk")}를 자동으로
          덮어쓰지 않습니다. 품명에 지원 류 신호가 있거나 {C("facts")}가 있으면 자동으로
          활성화됩니다. 제61·62류용 {C("apparel_tree_shadow")}, 제64류용
          {C("footwear_tree_shadow")}, 제84류용 {C("machinery_tree_shadow")}는 호환 별칭입니다.
        </div>

        <h2 id="sec-6" style={S.h2}>
          에러 응답
        </h2>
        <p style={S.p}>
          배치 전체가 거부되는 경우입니다(개별 항목 오류는 §5 참고). 상태 코드로
          구분하세요.
        </p>
        <pre style={S.preSolo}>{`// 401 — 키 누락/무효/폐기
{ "success": false, "reason": "missing_token", "message": "인증 토큰이 없습니다." }

// 400 — items 누락 / 비배열 / 빈배열 / 20개 초과 / 잘못된 옵션
{ "error": "too many items: 21 > sync limit 20" }

// 500 — 서버 오류
{ "error": "internal error" }`}</pre>

        <h2 id="sec-7" style={S.h2}>
          버전 · 문의
        </h2>
        <p style={S.p}>
          본 API 는 <b>v1 계약 고정</b>입니다 — 하위 호환이 깨지는 변경은 v2 로
          제공합니다. 무료 베타 이후 유료 플랜 전환 시 사전 공지합니다.
        </p>
        <p style={S.p}>
          <a
            href="/?page=contact"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("contact");
            }}
          >
            연동 문의
          </a>
        </p>
      </div>
    </ApiPortalLayout>
  );
}
